import { Injectable, Injector } from '@angular/core';
import { ApplicationService } from '@app/shared/application/application.service';
import { CodeService } from '../common/code/code.service';
import { ProfessionConfigurationService } from '@app/shared/services/profession-configuration.service';
import { forkJoin, Observable, of } from 'rxjs';
import { delay, map, switchMap, tap } from 'rxjs/operators';
import {
    ApplicationServiceProxy,
    ApplicationType,
    CreateOrUpdateRlaAcademicTitleInput,
    CreateOrUpdateRlaAcademicTitleOutput,
    CreateOrUpdateRlaApplicantPersonInput,
    CreateOrUpdateRlaBusinessInput,
    CreateOrUpdateRlaContactInput,
    CreateOrUpdateRlaPersonInput,
    CreateOrUpdateRlaReplacementInput,
    CreateRlaInput,
    CreateRlaOutput,
    ExternalSystemEnum,
    GeneralFileDto,
    GeneralFileInfoDto,
    GetRlaOutput,
    LiaTypeServiceProxy,
    ProfessionRegisterAffiliationServiceProxy,
    RlaAcademicTitleDto,
    RlaAcademicTitleServiceProxy,
    RlaApplicantPersonDto,
    RlaApplicantPersonServiceProxy,
    RlaBusinessDto,
    RlaBusinessServiceProxy,
    RlaContactDto,
    RlaContactServiceProxy,
    RlaDocumentGroup,
    RlaDto,
    RlaPersonDto,
    RlaPersonServiceProxy,
    RlaReplacementDto,
    RlaReplacementServiceProxy,
    RlaServiceProxy,
    RlaStatus,
    VisibilityServiceProxy,
    ApplicationUploadFilesInput,
    RlaInvoiceAddressDto,
    CreateOrUpdateRlaInvoiceAddressInput,
    RlaInvoiceAddressServiceProxy,
    ProfessionalLicenceDto
} from '../../../shared/service-proxies/service-proxies';
import { SelectItem } from '@node_modules/primeng/api';
import { AppConsts } from '@shared/AppConsts';

@Injectable()
export class RlaService extends ApplicationService {
    rla: RlaDto;
    healthRegisterSystem: ExternalSystemEnum;

    constructor(
        injector: Injector,
        private appService: ApplicationServiceProxy,
        codeService: CodeService,
        private professionConfigurationService: ProfessionConfigurationService,
        private professionRegisterService: ProfessionRegisterAffiliationServiceProxy,
        private liaTypeService: LiaTypeServiceProxy,
        private rlaServiceProxy: RlaServiceProxy,
        private rlaApplicantPersonServiceProxy: RlaApplicantPersonServiceProxy,
        private rlaPersonServiceProxy: RlaPersonServiceProxy,
        private rlaContactServiceProxy: RlaContactServiceProxy,
        private rlaAcademicTitleServiceProxy: RlaAcademicTitleServiceProxy,
        private rlaReplacementServiceProxy: RlaReplacementServiceProxy,
        private rlaBusinessServiceProxy: RlaBusinessServiceProxy,
        private rlaInvoiceAddressServiceProxy: RlaInvoiceAddressServiceProxy,
        private visibilityServiceProxy: VisibilityServiceProxy,
    ) {
        super(injector, codeService, appService);
    }

    getActiveProfessions(): Observable<string[]> {
        return this.liaTypeService.getActiveProfessions();
    }

    getExternalSystemByProfessionId(professionId: string): Observable<ExternalSystemEnum> {
        return this.liaTypeService.getExternalRegisterByProfessionId(professionId);
    }

    createRla(input: CreateRlaInput): Observable<CreateRlaOutput> {
        return this.rlaServiceProxy.createRla(input).pipe(tap(() => {
            this.rla = null;
        }));
    }

    getRla(caseId: string): Observable<GetRlaOutput> {
        this.rla = null;
        return this.getApplication(caseId).pipe(
            switchMap(() => this.rlaServiceProxy.getRla(caseId).pipe(tap((output: GetRlaOutput) => {
                this.rla = output.rla;
                return forkJoin([this.professionRegisterService.getAll(), of(output.rla)]).subscribe(([affiliations, rla]) => {
                    this.healthRegisterSystem = affiliations.find((a) => a.id === rla.professionId)?.externalSystem;
                });
            })))
        );
    }

    getMyRlas(): Observable<RlaDto[]> {
        return this.rlaServiceProxy.getMyRlas();
    }

    releaseRla(comment: string): Observable<void> {
        return this.rlaServiceProxy.releaseRla(this.caseId, comment);
    }

    getApplicantName(): Observable<string> {
        const p = this.rla?.applicantPerson;
        return of(`${p?.firstName || ''} ${p?.lastName || ''}`.trim());
    }

    getApplicantPerson(): Observable<RlaApplicantPersonDto> {
        return of(this.rla.applicantPerson).pipe(delay(0));
    }

    createOrUpdateApplicantPerson(input: CreateOrUpdateRlaApplicantPersonInput): Observable<RlaStatus> {
        this.setStepToReview(RlaStatus.ApplicantPerson);
        return this.rlaApplicantPersonServiceProxy.createOrUpdate(input).pipe(tap((status: RlaStatus) => {
            this.rla.applicantPerson = input.applicantPerson;
            this.rla.status = status;
        }));
    }

    getPerson(): Observable<RlaPersonDto> {
        return of(this.rla.person).pipe(delay(0));
    }

    createOrUpdatePerson(input: CreateOrUpdateRlaPersonInput): Observable<RlaStatus> {
        this.setStepToReview(RlaStatus.Person);
        return this.rlaPersonServiceProxy.createOrUpdate(input).pipe(tap((status: RlaStatus) => {
            this.rla.person = input.person;
            this.rla.status = status;
        }));
    }

    getContact(): Observable<RlaContactDto> {
        return of(this.rla.contact).pipe(delay(0));
    }

    createOrUpdateContact(input: CreateOrUpdateRlaContactInput): Observable<RlaStatus> {
        this.setStepToReview(RlaStatus.Contact);
        return this.rlaContactServiceProxy.createOrUpdate(input).pipe(tap((status: RlaStatus) => {
            this.rla.contact = input.contact;
            this.rla.status = status;
        }));
    }

    getAcademicTitles(): Observable<RlaAcademicTitleDto[]> {
        return of(this.rla.academicTitles || []).pipe(delay(0));
    }

    createBlankAcademicTitle(): Observable<string> {
        return this.rlaAcademicTitleServiceProxy.createBlank(this.caseId);
    }

    deleteAcademicTitle(id: string): Observable<void> {
        return this.rlaAcademicTitleServiceProxy.delete(this.caseId, id);
    }

    createOrUpdateAcademicTitles(input: CreateOrUpdateRlaAcademicTitleInput): Observable<RlaStatus> {
        this.setStepToReview(RlaStatus.AcademicTitle);
        return this.rlaAcademicTitleServiceProxy.createOrUpdate(input).pipe(tap((output: CreateOrUpdateRlaAcademicTitleOutput) => {
            this.rla.academicTitles = output.academicTitles;
            this.rla.status = output.status;
        }), map((output) => output.status));
    }

    getAcademicTitleFiles(forceRefresh?: boolean): Observable<RlaDocumentGroup[]> {
        if (forceRefresh) {
            return this.rlaAcademicTitleServiceProxy.getFiles(this.caseId);
        }
        return of([]);
    }

    uploadAcademicTitleFiles(academicTitleId: string, input: ApplicationUploadFilesInput): Observable<GeneralFileInfoDto[]> {
        return this.rlaAcademicTitleServiceProxy.uploadFiles(academicTitleId, input);
    }

    getAcademicTitleFile(fileId: number): Observable<GeneralFileDto> {
        return this.getAuthorityFile(fileId);
    }

    deleteAcademicTitleFile(fileId: number): Observable<void> {
        return this.deleteAuthorityFile(fileId);
    }

    getReplacement(): Observable<RlaReplacementDto> {
        return of(this.rla.replacement).pipe(delay(0));
    }

    createOrUpdateReplacement(input: CreateOrUpdateRlaReplacementInput): Observable<RlaStatus> {
        this.setStepToReview(RlaStatus.Replacement);
        return this.rlaReplacementServiceProxy.createOrUpdate(input).pipe(tap((status: RlaStatus) => {
            this.rla.replacement = input.replacement;
            this.rla.status = status;
        }));
    }

    getBusiness(): Observable<RlaBusinessDto> {
        return of(this.rla.business).pipe(delay(0));
    }

    createOrUpdateBusiness(input: CreateOrUpdateRlaBusinessInput): Observable<RlaStatus> {
        this.setStepToReview(RlaStatus.Business);
        return this.rlaBusinessServiceProxy.createOrUpdate(input).pipe(tap((status: RlaStatus) => {
            this.rla.business = input.business;
            this.rla.status = status;
        }));
    }

    getEvaluationComments(): Observable<string> {
        return this.rlaServiceProxy.getEvaluationComments(this._caseId);
    }

    checkDocuments(): Observable<RlaStatus[]> {
        return of([]);
    }

    getNextStatus(status: RlaStatus): Observable<RlaStatus> {
        const isAuthority = this.permission.isGranted('Pages.Authority.Applications');
        return this.visibilityServiceProxy.getVisibility(ApplicationType.Rla, this.caseId).pipe(map((output) => {
            const steps = output.items.map((x) => x.status as RlaStatus);
            const currentIndex = steps.findIndex((x) => x === status);
            const next = currentIndex >= 0 && currentIndex < steps.length - 1 ? steps[currentIndex + 1] : status;
            if (isAuthority && next === RlaStatus.Release) {
                return RlaStatus.FinishReview;
            }
            return next;
        }));
    }

    getStepUrl(nextStep: RlaStatus): string {
        return this.getNextStep(this.getUrlForStep(nextStep));
    }

    getUrlForStep(step: number): string {
        switch (step) {
            case RlaStatus.ApplicantPerson:
                return 'applicant-person';
            case RlaStatus.Person:
                return 'person';
            case RlaStatus.Contact:
                return 'contact';
            case RlaStatus.AcademicTitle:
                return 'academic-title';
            case RlaStatus.Replacement:
                return 'replacement';
            case RlaStatus.Business:
                return 'business';
            case RlaStatus.InvoiceAddress:
                return 'invoice-address';
            case RlaStatus.Release:
                return 'release';
            case RlaStatus.FinishReview:
                return 'finish-review';
            default:
                return null;
        }
    }

    createAddressDropdownList(): SelectItem[] {
        let addresses: SelectItem[] = [];

        addresses.push({ value: AppConsts.codes.address.private, label: this.l('Address-Private') + ': ' + this.getPrivateAddressDescription(this.rla.contact, this.rla.person) });
        addresses.push({ value: AppConsts.codes.address.business, label: this.l('Address-Business') + ': ' + this.getEmploymentAddressDescription(this.rla.business) });
        addresses.push({ value: AppConsts.codes.address.invoice, label: this.l(AppConsts.codes.address.invoice) });

        return addresses;
    }

    private getPrivateAddressDescription(contact: RlaContactDto, person: RlaPersonDto) {
        let str = person?.firstName ?? '';
        str = str?.concat(' ');
        str = str?.concat(person?.lastName ?? '');
        str = str?.concat(', ');
        str = str?.concat(contact?.street ?? '');

        if (contact?.streetNr && contact.streetNr.length > 0) {
            str = str?.concat(' ');
            str = str?.concat(contact.streetNr);
        }

        str = str?.concat(', ');
        str = str?.concat(contact?.city ?? '');

        return str;
    }

    private getEmploymentAddressDescription(business: RlaBusinessDto) {
        let str = business?.name;

        if (business.street !== undefined && business.street.length > 0) {
            if (str !== undefined && str.length > 0) {
                str = str.concat(', ');
            }

            str = str?.concat(business.street);

            if (business.streetNr !== undefined && business.streetNr !== null && business.streetNr.length > 0) {
                str = str?.concat(' ');
                str = str?.concat(business.streetNr);
            }
        }

        if (business.city !== undefined && business.city !== null && business.city?.length > 0) {
            if (str !== undefined && str.length > 0) {
                str = str.concat(', ');
            }

            str = str?.concat(business.city);
        }

        return str;
    }

    getInvoiceAddress(): Observable<RlaInvoiceAddressDto> {
        return of(this.rla.invoiceAddress).pipe(delay(1));
    }

    getInvoiceAddressDetails(): Observable<RlaInvoiceAddressDto> {
        return of(this.rla.invoiceAddress).pipe(delay(0));
    }

    createOrUpdateRlaInvoiceAddress(input: CreateOrUpdateRlaInvoiceAddressInput): Observable<void> {
        this.setStepToReview(RlaStatus.InvoiceAddress);
            return this.rlaInvoiceAddressServiceProxy.createOrUpdate(input).pipe(tap((status: RlaStatus) => {
                this.rla.invoiceAddress = input.invoiceAddress;
                this.rla.status = status;
            }))
                .pipe(map((status: RlaStatus) => void 0));
    }

    getProfessionalLicencePersonIdForApplication(): Observable<ProfessionalLicenceDto> {
        return this.rlaServiceProxy.getProfessionalLicenceForRla(this._caseId);
    }

    get caseId(): string {
        return this.rla.application.caseId;
    }

    get professionId(): string {
        return this.rla?.professionId;
    }

    get gln(): string {
        return this.rla?.person?.gln;
    }
}
